import { css } from '@emotion/react';
import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { SplitComponent } from '~/_shared/components/split/split.component';
import {
  TopUnderbarContainer, TopUnderbarType,
} from '~/_shared/components/topUnderbar/topUnderbar.container';
import { useHookWithRefCallback } from '~/_shared/utils/hooks/useHookWithRefCallback';
import { MapComponent } from '~/map/map/map.component';
import { LeftSideMapToolsContainer } from '~/map/mapToolControls/leftSideMapTools/leftSideMapTools.container';
import { MapPageSplitScreenTab } from '~/map/page/splitScreen/mapPageSplitScreen.types';
import { useGetRightSidebarContent } from '~/sidebar/sidebarApps/rightSidebar/helpers/useGetRightSidebarContent';
import { RightSidebarContainer } from '~/sidebar/sidebarApps/rightSidebar/rightSidebar.container';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { useSplitPanelState } from '~/store/userData/settings/splitPanel/useSplitPanelState';
import { ChatButtonComponent } from '../mapToolControls/chatButton/chatButton.component';
import { RightSideMapToolsContainer } from '../mapToolControls/rightSideMapTools/rightSideMapTools.container';
import { MapSearchContainer } from '../search/mapSearch.container';
import { MapStatusBarContainer } from '../statusBar/mapStatusBar.container';
import {
  getDefaultSplitPanelContentHeight, getMapMinHeight,
} from './mapPage.helpers';
import { MapPageSplitScreenGutterContainer } from './splitScreen/mapPageSplitScreenGutterContainer';
import { SplitScreenContent } from './splitScreen/splitScreenContent';
import { useHandleUserEventSequences } from './useHandleUserEventSequences.hook';
import { useShowUserLocationDefault } from './useShowUserLocationDefault.hook';

export const SPLIT_SCREEN_GUTTER_SIZE = 42;

const wrapperStyle = css({
  overflow: 'hidden',
  overflowY: 'auto',
  height: '100%',
  flex: '1 1 100%',
  display: 'flex',
});

const splitPanelStyle = css({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
});

const splitPanelSpreadsheetStyle = css({
  maxHeight: '100%',
});

const scrollbarStyles = {
  minHeight: '100%',
  maxHeight: '100%',
};

const mapWrapperStyle = css({
  position: 'relative',
  overflow: 'hidden',
});

const mapElementsWrapperStyle = css({
  flex: '1',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const mapSearchStyle = css({
  position: 'absolute',
  top: 10,
  left: 'calc(50% - 32px)', // -32px is to account for the button icons on the right
  transform: 'translateX(-50%)',
});

const splitWrapperStyle = css({
  height: '100%',
});

const splitContainerStyle = css({
  flex: 1,
  overflow: 'hidden',
});

const rightSidebarStyle = css({
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'flex-end',
});

const gutterContainerStyle = ({ isActive }: { isActive: boolean }) => css({
  display: isActive ? 'flex' : 'none',
});

const topUnderbarVisibleItems = new Set([
  TopUnderbarType.MAP_SETTINGS_STATUS,
  TopUnderbarType.EXPORT_IMAGE_STATUS,
  TopUnderbarType.EXPORT_REPORT_STATUS,
  TopUnderbarType.PROXIMITY_EXPORT_STATUS,
  TopUnderbarType.GEOCODING_PROGRESS_ALERT,
  TopUnderbarType.WMS_OPTIMIZE_TERRITORIES_PROGRESS,
  TopUnderbarType.OPTIMIZE_TERRITORIES_PROGRESS,
  TopUnderbarType.ANNOUNCEMENTS,
  TopUnderbarType.SEARCH_ENTRIES,
  TopUnderbarType.TEAM_MAP_SHARE_INVITE,
]);

type MapPageProps = {
  splitPanelHeight: number | null;
  isMapOverlayUiHidden?: boolean;
  isSplitPanelActive: boolean;
  isMobileScreen: boolean;
  searchActive: boolean;
  showStatusBar: boolean;
  className?: string;

  onSplitPanelHeightChange: (size: number) => void;
  onSplitPanelClose: () => void;
};

export const MapPageComponent: FC<MapPageProps> = (props) => {
  const {
    className,
    searchActive,
    onSplitPanelHeightChange,
    onSplitPanelClose,
    isMapOverlayUiHidden,
    isMobileScreen,
    isSplitPanelActive,
    showStatusBar,
  } = props;

  const [splitPanelEl, setSplitPanelEl] = useHookWithRefCallback<HTMLDivElement>();
  const [defaultSplitPanelContentHeight, setDefaultSplitPanelContentHeight] = useState(0);
  const [mapMinHeight, setMapMinHeight] = useState(0);
  const [wrapperEl, setWrapperEl] = useHookWithRefCallback<HTMLDivElement>();
  const rightSidebarContents = useGetRightSidebarContent();
  const mapId = useMapIdSelector();
  const { tab } = useSplitPanelState(mapId);
  const isMapPresentational = useIsMapPresentationalSelector();

  const showSplitter = (isSplitPanelActive && !isMobileScreen) || (isMobileScreen && rightSidebarContents !== null);
  const splitPanelHeight = props.splitPanelHeight ?? defaultSplitPanelContentHeight;
  const splitPanelSize = showSplitter ? [100 - splitPanelHeight, splitPanelHeight] : [100, 0];

  useShowUserLocationDefault();
  useHandleUserEventSequences();

  const onSplitDragEnd = useCallback((sizes: number[]) => {
    if (sizes[1] !== undefined) {
      onSplitPanelHeightChange(sizes[1]);
    }
  }, [onSplitPanelHeightChange]);

  useEffect(() => {
    if (!wrapperEl || !splitPanelEl) {
      return;
    }

    setMapMinHeight(oldMapMinHeight =>
      Math.min(oldMapMinHeight, getMapMinHeight(splitPanelEl, wrapperEl, isMobileScreen)),
    );
    setDefaultSplitPanelContentHeight(oldSplitPanelHeight =>
      Math.max(oldSplitPanelHeight, getDefaultSplitPanelContentHeight(splitPanelEl, wrapperEl)),
    );

    const wrapperResizeObserver = new ResizeObserver(() => {
      setMapMinHeight(oldMapMinHeight =>
        Math.min(oldMapMinHeight, getMapMinHeight(splitPanelEl, wrapperEl, isMobileScreen)),
      );
      const newSplitPanelContentHeight = getDefaultSplitPanelContentHeight(splitPanelEl, wrapperEl);
      setDefaultSplitPanelContentHeight(oldSplitPanelHeight =>
        Math.max(oldSplitPanelHeight, newSplitPanelContentHeight),
      );
    });

    wrapperResizeObserver.observe(wrapperEl);
    wrapperResizeObserver.observe(splitPanelEl);

    return () => {
      wrapperResizeObserver.disconnect();
    };
  }, [wrapperEl, splitPanelEl, isMobileScreen]);

  return (
    <div
      className={className}
      css={wrapperStyle}
      ref={setWrapperEl}
    >
      <div css={mapElementsWrapperStyle}>
        <TopUnderbarContainer visibleItems={topUnderbarVisibleItems} />

        <div css={splitContainerStyle}>
          <SplitComponent
            css={splitWrapperStyle}
            sizes={splitPanelSize}
            minSize={[mapMinHeight, 0]}
            gutterSize={showSplitter ? SPLIT_SCREEN_GUTTER_SIZE : 0}
            direction="vertical"
            onDragEnd={onSplitDragEnd}
            expandToMin
            gutter={(
              <div css={gutterContainerStyle({ isActive: showSplitter })}>
                <MapPageSplitScreenGutterContainer
                  onClose={onSplitPanelClose}
                />
              </div>
            )}
          >
            <div css={mapWrapperStyle}>
              <MapComponent />

              {!isMapOverlayUiHidden && (
                <>
                  <div>
                    {searchActive && <MapSearchContainer css={mapSearchStyle} />}
                  </div>

                  <div>
                    <RightSideMapToolsContainer />
                    <LeftSideMapToolsContainer />
                    {!isMapPresentational && <ChatButtonComponent />}
                  </div>
                </>
              )}
            </div>

            <div>
              {!isMapOverlayUiHidden && (
                <OverlayScrollbarComponent
                  css={scrollbarStyles}
                  type={ScrollbarType.Vertical}
                >
                  <div
                    ref={setSplitPanelEl}
                    css={[
                      splitPanelStyle,
                      (tab === MapPageSplitScreenTab.Spreadsheet) && !isMobileScreen ?
                        splitPanelSpreadsheetStyle :
                        null,
                    ]}
                    data-testid={showSplitter ? 'split-panel-open' : 'split-panel-closed'}
                  >
                    {isMobileScreen && rightSidebarContents && <RightSidebarContainer />}
                    {!isMobileScreen && isSplitPanelActive && (
                      <SplitScreenContent />
                    )}
                  </div>
                </OverlayScrollbarComponent>
              )}
            </div>
          </SplitComponent>
        </div>

        {showStatusBar && !isMobileScreen && !isMapOverlayUiHidden &&
          <MapStatusBarContainer />
        }
      </div>

      {!isMobileScreen && !isMapOverlayUiHidden && (
        <div css={rightSidebarStyle}>
          <RightSidebarContainer />
        </div>
      )}
    </div>
  );
};
